import { defineMeta } from '@/registry/types';

export const textFieldMeta = defineMeta({
  name: 'TextField',
  level: 'atom',
  category: 'input',
  classification: 'mui-overrides',
  description: 'Single/multi-line text input with label, helper text and error states.',
  baseLibrary: '@mui/material',
  muiEquivalent: 'TextField',
  muiDocs: 'https://mui.com/material-ui/react-text-field/',
  a2uiEquivalent: 'TextInput',
  importPath: '@/atoms/TextField',
  optionalProps: [
    'label',
    'placeholder',
    'helperText',
    'error',
    'required',
    'disabled',
    'multiline',
    'value',
    'onChange',
    'size',
  ],
  props: [
    {
      name: 'size',
      type: "'small' | 'medium'",
      default: 'medium',
      description:
        'Outer height from `controlHeights`: small=28 (Button small), medium=36 (Button medium). Same `size` name as a sibling Button so an inline row matches.',
    },
  ],
  supportedStates: ['default', 'focus', 'filled', 'error', 'disabled', 'required'],
  designTokens: ['colors', 'radius', 'typography', 'controlHeights'],
  accessibility: [
    'label is associated automatically; avoid placeholder-only labeling.',
    'Use helperText + error to convey validation; consider aria-describedby.',
  ],
  usageExamples: ['<TextField label="Email" helperText="Required" />'],
  rules: [
    'The fill recesses one rung below whatever surface hosts the field, which is what makes it read as inset rather than as a box laid on top. It resolves from the surface scope, so the same field is correct in a page form, a card and a dialog without being told where it is.',
    'On `page` there is no rung below to recess into, so the field is transparent and a heavier outline carries the affordance instead. That is the intended appearance on that one rung, not a missing background.',
    'Pick `size` from context the same way as Button: default `medium` (36px) in forms and toolbars beside medium buttons; `small` (28px) only in dense toolbars and tables beside small buttons. There is no `large` — MUI TextField does not expose one.',
  ],
  antiPatterns: [
    'Do not rely on placeholder as the only label.',
    'Do not set a background on a field. It defeats the recession and pins the field to one context, so it will be wrong the first time it is used on another surface.',
  ],
  llmSafe: true,
  whenToUse: ['Collecting free-form text', 'Numbers, emails, search terms'],
  whenNotToUse: ['Choosing from known options (use Select/Autocomplete)'],
});

export default textFieldMeta;
